// Third Party
import { useEffect } from "react";
import { useRouter } from "next/router";
import { useSelector, useDispatch } from "react-redux";
import { ContentfulClient } from "@lululemon/ecom-pattern-library";
import Cookies from "js-cookie";
import { createSelector } from "reselect";

// LULULEMON
// import { publicConfig } from "@lululemon/ecom-common/src/util/public-config";
// import { selectPDPContentfulData } from "../selectors/product-data";
// import { setPDPContentfulData } from "../store/actions/pdp-actions";

const ERROR_RETRY_LIMIT = 3;

/**
 * Verify whether to allow draft Contentful API entries to be returned.
 * Allow draft entries if URL contains query string `?preview=1` and the
 * previewToken is available.
 * @returns {boolean}
 */
export const isContentfulPreview = (queryStringOrObj, previewToken) => {
  const isQueryString = typeof queryStringOrObj === "string";
  const queryObj = isQueryString
    ? qs.parse(queryStringOrObj)
    : queryStringOrObj;
  const hasPreviewParam = queryObj?.preview === "1";
  const hasPreviewToken = previewToken != null;

  return hasPreviewParam && hasPreviewToken;
};

/**
 * Returns the user's `UsrLocale` in the
 * standard format with dashes.
 *
 * @example
 * 'fr-CA', 'en-US', 'en-CA'
 *
 * @returns String Standardized Locale
 */
export function getUsrLocaleStandard() {
  // Regional Accuracy - Phase 4 - Remove UsrLocale cookie
  const locale = Cookies.get("UsrLocale") || "en_US"; // Fallback
  return locale.replace("_", "-");
}

const contenfulConfig = {
  accessToken: "RRamUe4e7kzBZypQ6IOE6hnvtlDP69mnDfjiUcztJqI",
  environmentId: "nonprod",
  previewToken: "JIyXqiFZ3ujcBU1kC3LT9wn41d992A9v3OHeC8w-tlI",
  spaceId: "gndladl3x575",
  microcopy: {
    accessToken: "FqxoWKTHz60q87PFl-S4w6qeNMpc_wOjuqKBjVwQYM8",
    environmentId: "nonprod",
    previewToken: "fT7qemxpesNW4RReVZXD0QhGw5wubOwdyV_XkEZjPAk",
    spaceId: "j9cxqjc2zykv"
  },
  digitalPantsWall: {
    accessToken: "u_1nGhS4B17CiJgNfU555sd8LA-7nDeAGSUcRVWiWHI",
    previewToken: "EkuFF5gOb3c_ALdL9QJz_icTNoOkiJz6_epzpy7k80c",
    environmentId: "nonprod",
    spaceId: "t1t2v379jabw"
  },
  navigation: {
    accessToken: "RRamUe4e7kzBZypQ6IOE6hnvtlDP69mnDfjiUcztJqI",
    environmentId: "nonprod",
    previewToken: "JIyXqiFZ3ujcBU1kC3LT9wn41d992A9v3OHeC8w-tlI",
    spaceId: "gndladl3x575"
  }
};

export const createContentfulClient = (args = {}) => {
  const { accessToken, environmentId, previewToken, spaceId } = contenfulConfig;

  return new ContentfulClient({
    accessToken,
    environmentId,
    previewToken,
    spaceId,
    locale: (Cookies.get("UsrLocale") || "").replace("_", "-"),
    isPreviewEnabled: false,
    ...args // ACCEL TODO 👈 we should be more explicit here...
  });
};

const selectPDPContentfulData = createSelector(
  [(state) => state.pdpContentful],
  (pdpContentful) => pdpContentful
);

const setPDPContentfulData = (data, productId) => (dispatch) => {
  dispatch({
    type: "SET_PDP_CONTENTFUL_DATA",
    payload: data,
    productId
  });
};

/**
 * Fetches Contentful Data for the product ID
 *
 * NOTE: This will go away when the PDP2 changes are implemented and we can
 * derive this data from the productData context.
 *
 * @param {String} productId
 * @returns {Object} Contentful object
 */
function usePDPContentfulData(productId = "") {
  const dispatch = useDispatch();
  const { query } = useRouter();
  const { previewToken } = contenfulConfig;
  const locale = getUsrLocaleStandard();
  const isPreviewEnabled = isContentfulPreview(query, previewToken);
  const contentfulClient = createContentfulClient({
    locale,
    isPreviewEnabled
  });

  const pdpContentful = useSelector(selectPDPContentfulData);
  const pdpContentfulData = pdpContentful?.[productId];

  useEffect(() => {
    const {
      loading: contentLoading,
      error: contentError,
      retryCount = 0
    } = pdpContentfulData || {};

    // check if the PDP context already has the contentfulData
    // If not loading or an error was caught, try to request data
    // else if data is available, return it.
    if (
      productId &&
      ((!pdpContentfulData && !contentLoading) ||
        (contentError && retryCount < ERROR_RETRY_LIMIT))
    ) {
      // set the state to loading so concurrent calls wont go out
      setPDPContentfulData({ loading: true, retryCount }, productId)(dispatch);
      contentfulClient
        .getFilteredEntries({
          contentType: "pdp",
          filters: { "fields.productId[match]": productId }
        })
        .then((data) => {
          // set the PDP state with the returned data
          setPDPContentfulData(data?.items?.[0], productId)(dispatch);
          // return the data to referrer
          return data?.items?.[0];
        })
        .catch((error) => {
          // set the PDP state with the error
          setPDPContentfulData(
            { error, retryCount: retryCount + 1 },
            productId
          )(dispatch);
        });
    }
  }, [productId]);

  return pdpContentfulData;
}

export default usePDPContentfulData;
